'use client';

import { AlertTriangle, RefreshCw, Phone } from 'lucide-react';

interface DataErrorScreenProps {
  transcriptError?: string | null;
  redApiError: string | null;
  onRetryTranscripts?: () => void;
  onRetryRedApi: () => void;
}

export function DataErrorScreen({
  transcriptError,
  redApiError,
  onRetryTranscripts,
  onRetryRedApi,
}: DataErrorScreenProps) {
  const hasTranscriptError = !!transcriptError;
  const hasRedApiError = !!redApiError;

  const failedService = hasTranscriptError && hasRedApiError
    ? 'Tax Transcripts and Case Analysis'
    : hasTranscriptError
      ? 'Tax Transcripts'
      : 'Case Analysis';

  const handleRetry = () => {
    if (hasTranscriptError && onRetryTranscripts) {onRetryTranscripts();}
    if (hasRedApiError) {onRetryRedApi();}
  };

  return (
    <div className="min-h-screen bg-linear-to-b from-slate-50 to-white flex items-center justify-center p-4">
      <div className="max-w-lg w-full">
        <div className="bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden">
          <div className="bg-linear-to-r from-amber-500 to-orange-500 px-6 py-8 text-center">
            <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">
              Unable to Load Data
            </h1>
            <p className="text-amber-100">
              {failedService} service is temporarily unavailable
            </p>
          </div>

          <div className="p-6 space-y-6">
            <p className="text-lg text-slate-600 text-center">
              We&apos;re having trouble connecting to our services. This is
              usually temporary — please try again.
            </p>

            {process.env.NODE_ENV === 'development' && (
              <div className="space-y-2 p-4 bg-slate-50 rounded-lg border border-slate-200">
                {hasTranscriptError && (
                  <p className="text-sm text-red-600 font-mono">Transcripts: {transcriptError}</p>
                )}
                {hasRedApiError && (
                  <p className="text-sm text-red-600 font-mono">RED API: {redApiError}</p>
                )}
              </div>
            )}

            <button
              onClick={handleRetry}
              className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-primary text-white rounded-xl text-lg font-semibold hover:bg-primary/90 transition-colors"
            >
              <RefreshCw className="w-5 h-5" />
              Try Again
            </button>

            <div className="pt-4 border-t border-slate-200 text-center">
              <p className="text-slate-500 mb-3">
                If this issue persists, please contact support:
              </p>
              <a
                href="tel:1-833-380-9040"
                className="inline-flex items-center gap-2 px-4 py-2 bg-slate-100 text-slate-700 rounded-lg font-medium hover:bg-slate-200 transition-colors"
              >
                <Phone className="w-4 h-4" />
                1-833-380-9040
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
